import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { FaSearch, FaEdit, FaSave, FaChevronLeft, FaChevronRight, FaFilter, FaTimes, FaEye, FaCalendar, FaDownload, FaUserPlus, FaSync } from 'react-icons/fa';
import { AuthContext } from '../../components/AuthContext';
import MasterSelection from '../../components/MasterSelection';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const TallyLedgerManagement = () => {
  const { user, loading } = useContext(AuthContext);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [tallyLedgers, setTallyLedgers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [tallyGroups, setTallyGroups] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Statistics state
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    withRecipients: 0,
    withoutRecipients: 0,
    assignedActive: 0,
    unassignedActive: 0,
    withGST: 0,
    withoutGST: 0
  });
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipientSelectionOpen, setRecipientSelectionOpen] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sync dark mode with localStorage every 500ms
  useEffect(() => {
    const interval = setInterval(() => {
      const currentMode = localStorage.getItem('darkMode') === 'true';
      setDarkMode(currentMode);
    }, 500);

    return () => clearInterval(interval);
  }, []);
  
  // Enhanced Filter states
  const [filters, setFilters] = useState({
    name: '',
    gstRegistrationType: null,
    recipientType: null,
    recipient: null,
    hasGST: null,
    tallyGroup: null,
    dateType: 'updatedAt',
    dateFrom: null,
    dateTo: null,
  });
  
  // Filter options for Select components
  const gstTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'Regular', label: 'Regular' },
    { value: 'Composition', label: 'Composition' },
    { value: 'Unregistered', label: 'Unregistered' }
  ];
  
  const recipientTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'Customer', label: 'Customer' },
    { value: 'Dealer', label: 'Dealer' }
  ];

  const tallyGroupOptions = [
    { value: '', label: 'All Groups' },
    ...tallyGroups.map(group => ({
      value: group._id,
      label: group.Name
    }))
  ];
  
  const hasGSTOptions = [
    { value: '', label: 'Any' },
    { value: 'true', label: 'Has GSTIN' },
    { value: 'false', label: 'No GSTIN' }
  ];
  
  const dateTypeOptions = [
    { value: 'updatedAt', label: 'Last Updated' },
    { value: 'createdAt', label: 'Created Date' }
  ];

  // Custom styles for react-select to fix z-index issues and support dark mode
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '42px',
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      borderColor: darkMode ? '#4b5563' : '#d1d5db',
      '&:hover': {
        borderColor: darkMode ? '#6b7280' : '#9ca3af'
      },
      '&:focus-within': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      zIndex: 9999
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (darkMode ? '#4b5563' : '#f3f4f6')
        : (darkMode ? '#374151' : '#ffffff'),
      color: darkMode ? '#f3f4f6' : '#111827',
      '&:active': {
        backgroundColor: darkMode ? '#6b7280' : '#e5e7eb'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#111827'
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#111827'
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6b7280'
    })
  };
  
  // Editing state for a TallyLedger
  const [editForm, setEditForm] = useState({
    recipientType: '',
    recipient: '',
  });
  
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    withCredentials: true,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    }
  });
  
  // Fetch TallyLedger data with enhanced filters
  const fetchTallyLedgers = async (page = currentPage) => {
    // Don't fetch if user is not loaded yet
    if (!user?.user?.company) {
      console.log('User or company not available yet. User:', user);
      return;
    }

    console.log('Starting to fetch tally ledgers for company:', user.user.company._id, 'page:', page);
    setDataLoading(true);
    try {
      // Build query parameters object
      const params = { page, limit: 10 };
      
      // Add company filter - most important
      params.company = user.user.company._id;
      
      console.log('Fetching tally ledgers with params:', params);
      
      // Add basic filters
      if (filters.name) params.name = filters.name;
      if (filters.gstRegistrationType?.value) params.gstRegistrationType = filters.gstRegistrationType.value;
      if (filters.recipientType?.value) params.recipientType = filters.recipientType.value;
      if (filters.recipient?.value) params.recipient = filters.recipient.value;
      if (filters.tallyGroup?.value) params.tallyGroupId = filters.tallyGroup.value;
      
      // Add advanced filters
      if (filters.hasGST?.value) params.hasGST = filters.hasGST.value;
      if (filters.dateType) params.dateType = filters.dateType;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom.toISOString();
      if (filters.dateTo) params.dateTo = filters.dateTo.toISOString();
      
      const response = await api.get(`/tally-ledgers`, { params });
      console.log('Tally ledgers response:', response.data);
      
      if (response.data.success) {
        const ledgers = response.data.tallyLedgers || [];
        console.log('Setting tally ledgers:', ledgers.length, 'records');
        setTallyLedgers(ledgers);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.currentPage || page);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch tally ledgers',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }
    } catch (error) {
      console.error('Error fetching tally ledgers:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Error fetching tally ledgers: ${errorMessage}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      setTallyLedgers([]); // Clear any existing data
    } finally {
      setDataLoading(false);
    }
  };
  
  // Fetch Customers and Dealers for dropdowns
  const fetchRecipients = async () => {
    try {
      const [customersResponse, dealersResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/customers/all`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/dealers/all`)
      ]);
      
      if (customersResponse.data && Array.isArray(customersResponse.data)) {
        setCustomers(customersResponse.data);
      }
      
      if (dealersResponse.data && Array.isArray(dealersResponse.data.dealers)) {
        setDealers(dealersResponse.data.dealers);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load customers and dealers',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    }
  };

  // Fetch Tally Groups for dropdown
  const fetchTallyGroups = async () => {
    try {
      const response = await api.get('/tally-groups', {
        params: { company: user?.user?.company }
      });
      
      if (response.data.success && Array.isArray(response.data.tallyGroups)) {
        setTallyGroups(response.data.tallyGroups);
      }
    } catch (error) {
      console.error('Error fetching tally groups:', error);
    }
  };

  // Fetch statistics based on current filters
  const fetchStatistics = async () => {
    if (!user?.user?.company) {
      console.log('Cannot fetch statistics: user company not available');
      return;
    }

    try {
      const params = { 
        company: user.user.company._id, 
        includeInactive: 'true',
        limit: 100000 // Get all records for accurate statistics
      };
      
      console.log('Fetching statistics with params:', params);
      
      // Add current filters to statistics query
      if (filters.name) params.name = filters.name;
      if (filters.gstRegistrationType?.value) params.gstRegistrationType = filters.gstRegistrationType.value;
      if (filters.recipientType?.value) params.recipientType = filters.recipientType.value;
      if (filters.recipient?.value) params.recipient = filters.recipient.value;
      if (filters.tallyGroup?.value) params.tallyGroupId = filters.tallyGroup.value;
      if (filters.hasGST?.value) params.hasGST = filters.hasGST.value;
      if (filters.dateType) params.dateType = filters.dateType;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom.toISOString();
      if (filters.dateTo) params.dateTo = filters.dateTo.toISOString();
      
      const response = await api.get(`/tally-ledgers`, { params });
      console.log('Statistics response:', response.data);
      
      if (response.data.success) {
        const ledgers = response.data.tallyLedgers || [];
        const activeLedgers = ledgers.filter(l => l.isActive !== false);
        const stats = {
          total: ledgers.length,
          active: activeLedgers.length,
          inactive: ledgers.filter(l => l.isActive === false).length,
          withRecipients: ledgers.filter(l => l.recipient).length,
          withoutRecipients: ledgers.filter(l => !l.recipient).length,
          assignedActive: activeLedgers.filter(l => l.recipient).length,
          unassignedActive: activeLedgers.filter(l => !l.recipient).length,
          withGST: ledgers.filter(l => l.GSTIN && l.GSTIN.trim() !== '').length,
          withoutGST: ledgers.filter(l => !l.GSTIN || l.GSTIN.trim() === '').length
        };
        console.log('Calculated statistics:', stats);
        setStatistics(stats);
      } else {
        console.error('Statistics fetch failed:', response.data.message);
        // Reset statistics to zero when no data
        setStatistics({
          total: 0,
          active: 0,
          inactive: 0,
          withRecipients: 0,
          withoutRecipients: 0,
          assignedActive: 0,
          unassignedActive: 0,
          withGST: 0,
          withoutGST: 0
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Reset statistics to zero on error
      setStatistics({
        total: 0,
        active: 0,
        inactive: 0,
        withRecipients: 0,
        withoutRecipients: 0,
        assignedActive: 0,
        unassignedActive: 0,
        withGST: 0,
        withoutGST: 0
      });
    }
  };
  
  // Initial data load - wait for authentication to complete
  useEffect(() => {
    if (!loading && user?.user?.company) {
      console.log('Authentication complete, loading data for company:', user.user.company._id);
      fetchTallyLedgers();
      fetchRecipients();
      fetchTallyGroups();
      fetchStatistics();
    } else if (!loading && !user) {
      console.log('No user authenticated');
    } else if (loading) {
      console.log('Still loading authentication...');
    } else {
      console.log('User authenticated but no company context available');
    }
  }, [user, loading]);
  
  // Handle filter change for text inputs
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  // Handle filter change for react-select components
  const handleSelectChange = (selectedOption, { name }) => {
    setFilters({
      ...filters,
      [name]: selectedOption
    });
  };
  
  // Handle date picker changes
  const handleDateChange = (date, name) => {
    setFilters({
      ...filters,
      [name]: date
    });
  };
  
  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchTallyLedgers(1);
    fetchStatistics();
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilters({
      name: '',
      gstRegistrationType: null,
      recipientType: null,
      recipient: null,
      hasGST: null,
      tallyGroup: null,
      dateType: 'updatedAt',
      dateFrom: null,
      dateTo: null,
    });
    setCurrentPage(1);
    fetchTallyLedgers(1);
    fetchStatistics();
  };
  
  // Toggle advanced filters visibility
  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(!showAdvancedFilters);
  };
  
  // Get customer/dealer options for recipient filter based on recipient type
  const getRecipientOptions = () => {
    if (filters.recipientType?.value === 'Customer') {
      return customers.map(customer => ({
        value: customer._id,
        label: `${customer.name} (${customer.customerCode})`
      }));
    } else if (filters.recipientType?.value === 'Dealer') {
      return dealers.map(dealer => ({
        value: dealer._id,
        label: `${dealer.businessName} (${dealer.id})`
      }));
    }
    return [{ value: '', label: 'Select recipient type first' }];
  };
  
  // Open ledger details modal
  const openDetailModal = (ledger) => {
    setSelectedLedger(ledger);
    setDetailModalOpen(true);
  };
  
  // Open recipient selection modal
  const openRecipientModal = (ledger) => {
    setSelectedLedger(ledger);
    setEditForm({
      recipientType: ledger.recipientType || '',
      recipient: ledger.recipient?._id || '',
    });
    setRecipientSelectionOpen(true);
  };

  // Handle recipient selection from RecipientSelection component
  const handleRecipientSelect = async (recipient, recipientType) => {
    try {
      const updateData = {
        recipientType: recipientType,
        recipient: recipient._id,
        company: user.user.company._id
      };

      const response = await api.patch(`/tally-ledgers/${selectedLedger._id}`, updateData);
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Recipient updated successfully',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
        
        // Refresh data
        fetchTallyLedgers();
        fetchStatistics();
        setRecipientSelectionOpen(false);
        setSelectedLedger(null);
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message,
        confirmButtonColor: '#3085d6'
      });
    }
  };

  // Handle ledger activation/deactivation
  const toggleLedgerStatus = async (ledger) => {
    const newStatus = !ledger.isActive;
    const action = newStatus ? 'activate' : 'deactivate';
    
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Ledger?`,
      text: `Are you sure you want to ${action} "${ledger.Name}"? ${!newStatus ? 'This will prevent it from being assigned as a recipient and appearing in prints.' : ''}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#10b981' : '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${action}!`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const updateData = {
          isActive: newStatus,
          company: user.user.company._id
        };

        const response = await api.patch(`/tally-ledgers/${ledger._id}`, updateData);
        
        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: `Ledger ${action}d successfully`,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
          
          // Refresh data
          fetchTallyLedgers();
          fetchStatistics();
        } else {
          throw new Error(response.data.message || 'Failed to update');
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || error.message,
          confirmButtonColor: '#3085d6'
        });
      }
    }
  };
  
  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value,
      // Clear recipient when recipientType changes
      ...(name === 'recipientType' ? { recipient: '' } : {})
    });
  };
  
  // Save TallyLedger changes
  const saveTallyLedger = async () => {
    try {
      const updateData = {
        ...editForm,
        company: user.user.company._id
      };
      
      const response = await api.patch(`/tally-ledgers/${selectedLedger._id}`, updateData);
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Tally Ledger updated successfully',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
        
        // Refresh data and close modal
        fetchTallyLedgers();
        setRecipientModalOpen(false);
        setSelectedLedger(null);
        setEditForm({
          recipientType: '',
          recipient: '',
        });
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message,
        confirmButtonColor: '#3085d6'
      });
    }
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTallyLedgers(page);
  };
  
  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    
    // Always show first page
    buttons.push(
      <button 
        key="first" 
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
          currentPage === 1 
            ? (darkMode 
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
              : 'bg-blue-500 text-white shadow-md')
            : (darkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
              : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300')
        }`}
      >
        1
      </button>
    );
    
    // If there are many pages, add ellipsis
    if (currentPage > 3) {
      buttons.push(
        <span key="ellipsis1" className={`px-2 py-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>...</span>
      );
    }
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      buttons.push(
        <button 
          key={i} 
          onClick={() => handlePageChange(i)}
          className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
            currentPage === i 
              ? (darkMode 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-blue-500 text-white shadow-md')
              : (darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300')
          }`}
        >
          {i}
        </button>
      );
    }
    
    // If there are many pages, add ellipsis
    if (currentPage < totalPages - 2) {
      buttons.push(
        <span key="ellipsis2" className={`px-2 py-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>...</span>
      );
    }
    
    // Always show last page if there is more than one page
    if (totalPages > 1) {
      buttons.push(
        <button 
          key="last" 
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
            currentPage === totalPages 
              ? (darkMode 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-blue-500 text-white shadow-md')
              : (darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300')
          }`}
        >
          {totalPages}
        </button>
      );
    }
    
    return buttons;
  };
  
  // Get available recipients based on selected recipientType
  const getAvailableRecipients = () => {
    if (editForm.recipientType === 'Customer') {
      return customers;
    } else if (editForm.recipientType === 'Dealer') {
      return dealers;
    }
    return [];
  };
  
  // Render recipient name based on type and ID
  const renderRecipientName = (recipientType, recipientId) => {
    if (!recipientType || !recipientId) return 'Not assigned';
    
    if (recipientType === 'Customer') {
      const customer = customers.find(c => c._id === recipientId);
      return customer ? customer.name : 'Unknown Customer';
    } else if (recipientType === 'Dealer') {
      const dealer = dealers.find(d => d._id === recipientId);
      return dealer ? dealer.businessName : 'Unknown Dealer';
    }
    
    return 'Not assigned';
  };

  // Detail Modal Component
  const DetailModal = () => {
    if (!selectedLedger) return null;
    
    // Find recipient details 
    const recipientDetails = selectedLedger.recipientType && selectedLedger.recipient ? 
      (selectedLedger.recipientType === 'Customer' ? 
        customers.find(c => c._id === selectedLedger.recipient._id) :
        dealers.find(d => d._id === selectedLedger.recipient._id)) 
      : null;
    
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
        <div className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex justify-between items-center sticky top-0 z-10 ${
            darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-100 border-gray-200'
          }`}>
            <h3 className={`text-lg sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Tally Ledger Details</h3>
            <button 
              onClick={() => setDetailModalOpen(false)}
              className={`p-2 rounded-xl transition-colors ${
                darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-4 sm:p-6">
            {/* Tally Ledger Information */}
            <div className="mb-8">
              <h4 className={`text-base sm:text-lg font-bold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full mr-2 ${
                  darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
                }`}>
                  Tally Ledger
                </span>
                <span className="truncate">{selectedLedger.Name}</span>
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h5 className={`text-sm sm:text-base font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Basic Information</h5>
                  
                  <div className={`space-y-3 p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID:</span>
                      <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedLedger.id}</span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name:</span>
                      <span className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedLedger.Name}</span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Parent:</span>
                      <span className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedLedger.parentName || selectedLedger.parent?.name || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email:</span>
                      <span className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedLedger.email || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Website:</span>
                      <span className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedLedger.website || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Created By:</span>
                      <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedLedger.createdBy || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Modified:</span>
                      <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {selectedLedger.updatedAt ? (() => {
                          const date = new Date(selectedLedger.updatedAt);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })() : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between gap-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedLedger.isActive !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedLedger.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-500 font-semibold">Closing Balance:</span>
                      <span className={`font-bold text-xl ${
                        selectedLedger.closingBalance > 0 ? 'text-green-600' : 
                        selectedLedger.closingBalance < 0 ? 'text-red-600' : 
                        'text-gray-500'
                      }`}>
                        ₹ {(selectedLedger.closingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h5 className="text-md font-medium text-gray-700 mb-3">GST & Address Details</h5>
                  
                  <div className="space-y-3 bg-gray-50 p-4 rounded-md">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">GSTIN:</span>
                      <span className="font-medium">{selectedLedger.GSTIN || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">GST Registration Type:</span>
                      <span className="font-medium">{selectedLedger.gstRegistrationType || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Address:</span>
                      <span className="font-medium">{selectedLedger.address || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">State:</span>
                      <span className="font-medium">{selectedLedger.state || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Pin Code:</span>
                      <span className="font-medium">{selectedLedger.pinCode || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Mailing Name:</span>
                      <span className="font-medium">{selectedLedger.mailingName || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Recipient Information */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">
                  Recipient
                </span>
                {selectedLedger.recipientType ? `${selectedLedger.recipientType} Information` : 'No Recipient Assigned'}
              </h4>
              
              {selectedLedger.recipientType && recipientDetails ? (
                <div className="bg-gray-50 p-4 rounded-md">
                  {/* Customer-specific fields */}
                  {selectedLedger.recipientType === 'Customer' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Customer Code:</span>
                          <span className="font-medium">{recipientDetails.customerCode}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Name:</span>
                          <span className="font-medium">{recipientDetails.name}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Phone:</span>
                          <span className="font-medium">{recipientDetails.phone}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Secondary Phone:</span>
                          <span className="font-medium">{recipientDetails.secondaryPhone || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Email:</span>
                          <span className="font-medium">{recipientDetails.email || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Site Address:</span>
                          <span className="font-medium">{recipientDetails.siteAddress || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Pin Code:</span>
                          <span className="font-medium">{recipientDetails.pinCode || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">PAN No:</span>
                          <span className="font-medium">{recipientDetails.panNo || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">GST No:</span>
                          <span className="font-medium">{recipientDetails.gstNo || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Dealer-specific fields */}
                  {selectedLedger.recipientType === 'Dealer' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Dealer ID:</span>
                          <span className="font-medium">{recipientDetails.id}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Business Name:</span>
                          <span className="font-medium">{recipientDetails.businessName}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Business Owner:</span>
                          <span className="font-medium">{recipientDetails.businessOwner || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Phone:</span>
                          <span className="font-medium">{recipientDetails.phone}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Secondary Phone:</span>
                          <span className="font-medium">{recipientDetails.secondaryPhone || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Email:</span>
                          <span className="font-medium">{recipientDetails.email || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Manager Name:</span>
                          <span className="font-medium">{recipientDetails.managerName || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Address:</span>
                          <span className="font-medium">{recipientDetails.address || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Pin Code:</span>
                          <span className="font-medium">{recipientDetails.pinCode || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">GST No:</span>
                          <span className="font-medium">{recipientDetails.gstNo || 'N/A'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">PAN No:</span>
                          <span className="font-medium">{recipientDetails.panNo || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-yellow-50 text-yellow-800 p-4 rounded-md flex items-center">
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                  </svg>
                  No recipient has been assigned to this ledger yet. Click "Edit Recipient" to assign one.
                </div>
              )}
              
              <div className="mt-4">
                {selectedLedger.isActive !== false ? (
                  <button
                    onClick={() => {
                      setDetailModalOpen(false);
                      openRecipientModal(selectedLedger);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <FaEdit className="inline mr-2" /> Edit Recipient
                  </button>
                ) : (
                  <div className="bg-red-50 text-red-800 p-3 rounded-md flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                    </svg>
                    Cannot assign recipient to inactive ledger. Activate the ledger first.
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setDetailModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Recipient Selection Modal
  const RecipientModal = () => {
    if (!selectedLedger) return null;
    
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
        <div className={`rounded-2xl shadow-2xl w-full max-w-md ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex justify-between items-center ${
            darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-100 border-gray-200'
          }`}>
            <h3 className={`text-base sm:text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Update Recipient</h3>
            <button 
              onClick={() => setRecipientModalOpen(false)}
              className={`p-2 rounded-xl transition-colors ${
                darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-4 sm:p-6">
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tally Ledger</label>
                <div className={`p-3 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedLedger.Name}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedLedger.id} | {selectedLedger.GSTIN || 'No GSTIN'}</p>
                </div>
              </div>
              
              <div>
                <label htmlFor="recipientType" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recipient Type</label>
                <select
                  id="recipientType"
                  name="recipientType"
                  value={editForm.recipientType}
                  onChange={handleEditFormChange}
                  className={`w-full rounded-xl px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } border`}
                >
                  <option value="">Select Type</option>
                  <option value="Customer">Customer</option>
                  <option value="Dealer">Dealer</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="recipient" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recipient</label>
                <select
                  id="recipient"
                  name="recipient"
                  value={editForm.recipient}
                  onChange={handleEditFormChange}
                  disabled={!editForm.recipientType}
                  className={`w-full rounded-xl px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white disabled:opacity-50'
                      : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                  } border`}
                >
                  <option value="">Select Recipient</option>
                  {getAvailableRecipients().map(recipient => (
                    <option key={recipient._id} value={recipient._id}>
                      {editForm.recipientType === 'Dealer' 
                        ? recipient.businessName
                        : recipient.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setRecipientModalOpen(false)}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  darkMode
                    ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={saveTallyLedger}
                className={`px-4 py-2 rounded-xl text-white font-medium transition-all ${
                  (!editForm.recipientType || !editForm.recipient)
                    ? (darkMode ? 'bg-gray-600 cursor-not-allowed' : 'bg-gray-400 cursor-not-allowed')
                    : (darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700')
                }`}
                disabled={!editForm.recipientType || !editForm.recipient}
              >
                <FaSave className="inline mr-2" /> Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Excel export functionality
  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Fetch all ledgers without pagination for export
      const response = await api.get('/tally-ledgers/export', {
        params: {
          ...(() => {
            const params = {};
            if (filters.name) params.name = filters.name;
            if (filters.gstRegistrationType?.value) params.gstRegistrationType = filters.gstRegistrationType.value;
            if (filters.recipientType?.value) params.recipientType = filters.recipientType.value;
            if (filters.recipient?.value) params.recipient = filters.recipient.value;
            if (filters.tallyGroup?.value) params.tallyGroupId = filters.tallyGroup.value;
            if (filters.hasGST?.value) params.hasGST = filters.hasGST.value;
            if (filters.dateType) params.dateType = filters.dateType;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom.toISOString();
            if (filters.dateTo) params.dateTo = filters.dateTo.toISOString();
            params.company = user.user.company._id;
            return params;
          })()
        },
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tally-ledgers-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Export Successful!',
        text: 'Tally ledgers exported successfully',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.response?.data?.message || 'Failed to export data',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setExporting(false);
    }
  };

  // Show loading screen while authentication is in progress
  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} p-6`}>
        <div className="flex items-center justify-center h-screen">
          <div className="flex flex-col items-center">
            <FaSync className={`animate-spin text-6xl ${darkMode ? 'text-blue-400' : 'text-blue-600'} mb-6`} />
            <p className={`text-xl font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Loading authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no user or company context
  if (!user?.user?.company) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} p-6`}>
        <div className="flex justify-center items-center h-screen">
          <div className={`text-xl font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
            Authentication required or no company context available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} p-6 transition-colors duration-200`}>
      <div className="container mx-auto">
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 p-4 sm:p-6 rounded-2xl ${darkMode ? 'bg-gray-800/80 backdrop-blur-sm border border-gray-700/50' : 'bg-white shadow-lg border border-gray-200'}`}>
        <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tally Ledger Management</h2>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => {
              fetchTallyLedgers();
              fetchStatistics();
            }}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium transition-all shadow-md ${
              darkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:shadow-blue-500/30'
                : 'bg-blue-500 hover:bg-blue-600 text-white hover:shadow-lg'
            }`}
            title="Refresh Data"
          >
            <FaSync className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={exportToExcel}
            disabled={exporting}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium transition-all shadow-md ${
              exporting 
                ? (darkMode ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                : (darkMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white hover:shadow-lg hover:shadow-green-500/30'
                  : 'bg-green-500 hover:bg-green-600 text-white hover:shadow-lg')
            }`}
          >
            <FaDownload className={exporting ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export to Excel'}</span>
            <span className="sm:hidden">{exporting ? '...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className={`mb-16 rounded-2xl p-8 border shadow-lg ${
        darkMode 
          ? 'bg-gray-800/60 backdrop-blur-sm border-gray-700/50' 
          : 'bg-gradient-to-br from-gray-50 to-blue-50/30 border-gray-100'
      }`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className={`text-2xl font-bold ${
                darkMode 
                  ? 'text-white' 
                  : 'bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent'
              }`}>
                Dashboard Overview
              </h3>
            </div>
            <p className={`text-sm ml-10 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Real-time insights and analytics for your tally ledgers
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className={`px-4 py-2.5 rounded-xl border shadow-sm ${
              darkMode
                ? 'bg-gray-700/50 backdrop-blur-sm border-gray-600/50'
                : 'bg-white/70 backdrop-blur-sm border-white/50'
            }`}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Live Data</span>
              </div>
            </div>
            <div className={`px-4 py-2.5 rounded-xl border shadow-sm ${
              darkMode
                ? 'bg-blue-900/30 border-blue-700/60'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/60'
            }`}>
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  Updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8 auto-rows-fr">
          {/* Total Ledgers */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-blue-500/10 to-blue-600/20' : 'from-blue-500/8 to-blue-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode 
                      ? 'text-white group-hover:text-blue-400' 
                      : 'text-gray-900 group-hover:text-blue-600'
                  }`}>
                    {statistics.total.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Records</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Total Ledgers</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-blue-300 bg-blue-900/40' : 'text-blue-600 bg-blue-50'
                  }`}>100%</span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: '100%' }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-green-500/10 to-emerald-600/20' : 'from-green-500/8 to-emerald-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-green-400'
                      : 'text-gray-900 group-hover:text-green-600'
                  }`}>
                    {statistics.active.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Status</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Active Ledgers</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-green-300 bg-green-900/40' : 'text-green-600 bg-green-50'
                  }`}>
                    {statistics.total > 0 ? Math.round((statistics.active / statistics.total) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.total > 0 ? (statistics.active / statistics.total) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Inactive */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-red-500/10 to-rose-600/20' : 'from-red-500/8 to-rose-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-red-400'
                      : 'text-gray-900 group-hover:text-red-600'
                  }`}>
                    {statistics.inactive.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Inactive Status</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Inactive Ledgers</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-red-300 bg-red-900/40' : 'text-red-600 bg-red-50'
                  }`}>
                    {statistics.total > 0 ? Math.round((statistics.inactive / statistics.total) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-red-500 to-rose-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.total > 0 ? (statistics.inactive / statistics.total) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Active */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-emerald-500/10 to-teal-600/20' : 'from-emerald-500/8 to-teal-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.25 0V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25h10.5a2.25 2.25 0 002.25-2.25V12z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-emerald-400'
                      : 'text-gray-900 group-hover:text-emerald-600'
                  }`}>
                    {statistics.assignedActive.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Assigned</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Assigned Active</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-emerald-300 bg-emerald-900/40' : 'text-emerald-600 bg-emerald-50'
                  }`}>
                    {statistics.active > 0 ? Math.round((statistics.assignedActive / statistics.active) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.active > 0 ? (statistics.assignedActive / statistics.active) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Unassigned Active */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-amber-500/10 to-orange-600/20' : 'from-amber-500/8 to-orange-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-amber-400'
                      : 'text-gray-900 group-hover:text-amber-600'
                  }`}>
                    {statistics.unassignedActive.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Unassigned</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Unassigned Active</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-amber-300 bg-amber-900/40' : 'text-amber-600 bg-amber-50'
                  }`}>
                    {statistics.active > 0 ? Math.round((statistics.unassignedActive / statistics.active) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-amber-500 to-orange-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.active > 0 ? (statistics.unassignedActive / statistics.active) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* With Recipients */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-purple-500/10 to-violet-600/20' : 'from-purple-500/8 to-violet-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-purple-400'
                      : 'text-gray-900 group-hover:text-purple-600'
                  }`}>
                    {statistics.withRecipients.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>With Recipients</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>With Recipients</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-purple-300 bg-purple-900/40' : 'text-purple-600 bg-purple-50'
                  }`}>
                    {statistics.total > 0 ? Math.round((statistics.withRecipients / statistics.total) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-purple-500 to-violet-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.total > 0 ? (statistics.withRecipients / statistics.total) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* No Recipients */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-gray-500/10 to-slate-600/20' : 'from-gray-500/8 to-slate-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-gray-500 to-slate-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-gray-400'
                      : 'text-gray-900 group-hover:text-gray-600'
                  }`}>
                    {statistics.withoutRecipients.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No Recipients</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>No Recipients</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-gray-300 bg-gray-700/50' : 'text-gray-600 bg-gray-50'
                  }`}>
                    {statistics.total > 0 ? Math.round((statistics.withoutRecipients / statistics.total) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-gray-500 to-slate-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.total > 0 ? (statistics.withoutRecipients / statistics.total) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* With GST */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-yellow-500/10 to-amber-600/20' : 'from-yellow-500/8 to-amber-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-yellow-400'
                      : 'text-gray-900 group-hover:text-yellow-600'
                  }`}>
                    {statistics.withGST.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>GST Registered</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>With GST</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-yellow-300 bg-yellow-900/40' : 'text-yellow-600 bg-yellow-50'
                  }`}>
                    {statistics.total > 0 ? Math.round((statistics.withGST / statistics.total) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-yellow-500 to-amber-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.total > 0 ? (statistics.withGST / statistics.total) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* No GST */}
          <div className={`group relative backdrop-blur-sm border rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
            darkMode
              ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800/80'
              : 'bg-white/90 border-gray-200/60'
          }`}>
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              darkMode ? 'from-orange-500/10 to-red-600/20' : 'from-orange-500/8 to-red-600/12'
            }`}></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-6">
                <div className="p-4 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="text-right space-y-1">
                  <p className={`text-4xl font-bold transition-colors duration-300 ${
                    darkMode
                      ? 'text-white group-hover:text-orange-400'
                      : 'text-gray-900 group-hover:text-orange-600'
                  }`}>
                    {statistics.withoutGST.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Not Registered</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>No GST</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    darkMode ? 'text-orange-300 bg-orange-900/40' : 'text-orange-600 bg-orange-50'
                  }`}>
                    {statistics.total > 0 ? Math.round((statistics.withoutGST / statistics.total) * 100) : 0}%
                  </span>
                </div>
                <div className="relative">
                  <div className={`w-full rounded-full h-3 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="bg-gradient-to-r from-orange-500 to-red-600 h-3 rounded-full shadow-sm transition-all duration-1000 ease-out" 
                         style={{ width: `${statistics.total > 0 ? (statistics.withoutGST / statistics.total) * 100 : 0}%` }}>
                      <div className="h-full bg-gradient-to-r from-white/20 to-transparent rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filter Card */}
      <div className={`${darkMode ? 'bg-gray-800/80 backdrop-blur-sm border border-gray-700/50' : 'bg-white'} shadow-lg rounded-2xl overflow-hidden mb-8`}>
        <div className={`${darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-100 border-gray-200'} px-4 sm:px-6 py-3 sm:py-4 border-b`}>
          <h3 className={`text-base sm:text-lg lg:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <FaFilter className="inline mr-2" /> Filters
          </h3>
        </div>
        <div className="p-4 sm:p-6">
          <form onSubmit={applyFilters}>
            {/* Basic Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
              <div>
                <label htmlFor="name" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                  placeholder="Search by name"
                  className={`w-full rounded-xl px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } border`}
                />
              </div>
              <div>
                <label htmlFor="gstRegistrationType" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>GST Type</label>
                <Select
                  id="gstRegistrationType"
                  name="gstRegistrationType"
                  value={filters.gstRegistrationType}
                  onChange={(option) => handleSelectChange(option, { name: 'gstRegistrationType' })}
                  options={gstTypeOptions}
                  placeholder="Select GST Type"
                  className="basic-single"
                  classNamePrefix="select"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  isClearable
                />
              </div>
              <div>
                <label htmlFor="recipientType" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recipient Type</label>
                <Select
                  id="recipientType"
                  name="recipientType"
                  value={filters.recipientType}
                  onChange={(option) => handleSelectChange(option, { name: 'recipientType' })}
                  options={recipientTypeOptions}
                  placeholder="Select Recipient Type"
                  className="basic-single"
                  classNamePrefix="select"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  isClearable
                />
              </div>
              <div>
                <label htmlFor="tallyGroup" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tally Group</label>
                <Select
                  id="tallyGroup"
                  name="tallyGroup"
                  value={filters.tallyGroup}
                  onChange={(option) => handleSelectChange(option, { name: 'tallyGroup' })}
                  options={tallyGroupOptions}
                  placeholder="Select Tally Group"
                  className="basic-single"
                  classNamePrefix="select"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  isClearable
                />
              </div>
            </div>
            
            {/* Filter Actions */}
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                type="submit"
                className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-semibold transition-all shadow-md ${
                  darkMode
                    ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:shadow-blue-500/30'
                    : 'bg-blue-500 hover:bg-blue-600 text-white hover:shadow-lg'
                }`}
              >
                <FaSearch className="w-4 h-4" /> <span className="hidden sm:inline">Apply Filters</span><span className="sm:hidden">Apply</span>
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-semibold transition-all shadow-md ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white hover:shadow-lg'
                    : 'bg-gray-500 hover:bg-gray-600 text-white hover:shadow-md'
                }`}
              >
                <FaTimes className="w-4 h-4" /> Clear
              </button>
              <button
                type="button"
                onClick={toggleAdvancedFilters}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-semibold transition-all shadow-md ${
                  darkMode
                    ? 'bg-purple-600 hover:bg-purple-700 text-white hover:shadow-lg hover:shadow-purple-500/30'
                    : 'bg-purple-500 hover:bg-purple-600 text-white hover:shadow-md'
                }`}
              >
                <FaFilter className="w-4 h-4" /> 
                <span className="hidden sm:inline">{showAdvancedFilters ? 'Hide Advanced' : 'Advanced Filters'}</span>
                <span className="sm:hidden">{showAdvancedFilters ? 'Hide' : 'Advanced'}</span>
              </button>
            </div>
            
            {/* Advanced Filters - Conditionally displayed */}
            {showAdvancedFilters && (
              <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <FaCalendar className="w-4 h-4" />
                  Advanced Filters
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="hasGST" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>GSTIN Status</label>
                    <Select
                      id="hasGST"
                      name="hasGST"
                      value={filters.hasGST}
                      onChange={(option) => handleSelectChange(option, { name: 'hasGST' })}
                      options={hasGSTOptions}
                      placeholder="Select GSTIN Status"
                      className="basic-single"
                      classNamePrefix="select"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      isClearable
                    />
                  </div>
                
                <div>
                  <label htmlFor="dateType" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Filter by Date</label>
                  <Select
                    id="dateType"
                    name="dateType"
                    value={dateTypeOptions.find(opt => opt.value === filters.dateType)}
                    onChange={(option) => setFilters({...filters, dateType: option.value})}
                    options={dateTypeOptions}
                    className="basic-single"
                    classNamePrefix="select"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
                
                <div>
                  <label htmlFor="dateFrom" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>From Date</label>
                  <div className="relative">
                    <DatePicker
                      id="dateFrom"
                      selected={filters.dateFrom}
                      onChange={(date) => handleDateChange(date, 'dateFrom')}
                      className={`w-full rounded-xl px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        darkMode
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } border`}
                      placeholderText="Select start date"
                      dateFormat="MM/dd/yyyy"
                      isClearable
                    />
                    <div className={`absolute right-2 top-2.5 pointer-events-none ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                      <FaCalendar />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="dateTo" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>To Date</label>
                  <div className="relative">
                    <DatePicker
                      id="dateTo"
                      selected={filters.dateTo}
                      onChange={(date) => handleDateChange(date, 'dateTo')}
                      className={`w-full rounded-xl px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        darkMode
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } border`}
                      placeholderText="Select end date"
                      dateFormat="MM/dd/yyyy"
                      isClearable
                      minDate={filters.dateFrom}
                    />
                    <div className={`absolute right-2 top-2.5 pointer-events-none ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                      <FaCalendar />
                    </div>
                  </div>
                </div>
                
                </div>
                
                {/* Recipient Filter - only show if recipientType is selected */}
                {filters.recipientType && (
                  <div className="mt-4">
                    <label htmlFor="recipient" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Specific Recipient</label>
                    <Select
                      id="recipient"
                      name="recipient"
                      value={filters.recipient}
                      onChange={(option) => handleSelectChange(option, { name: 'recipient' })}
                      options={getRecipientOptions()}
                      placeholder={`Select ${filters.recipientType?.value}`}
                      className="basic-single"
                      classNamePrefix="select"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      isClearable
                      isDisabled={!filters.recipientType}
                    />
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
      
      {/* Table Card */}
      <div className={`${darkMode ? 'bg-gray-800/80 backdrop-blur-sm border border-gray-700/50' : 'bg-white'} shadow-lg rounded-2xl overflow-hidden`}>
        <div className={`${darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-100 border-gray-200'} px-4 sm:px-6 py-3 sm:py-4 border-b`}>
          <h3 className={`text-base sm:text-lg lg:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tally Ledgers</h3>
        </div>
        <div className="p-4 sm:p-6">
          {dataLoading ? (
            <div className="flex items-center justify-center py-12">
              <FaSync className={`animate-spin text-4xl ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
          ) : (
            <>
              {/* Mobile view with simplified cards */}
              <div className="block lg:hidden space-y-4">
                {tallyLedgers.length === 0 ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No tally ledgers found
                  </div>
                ) : (
                  tallyLedgers.map((ledger) => (
                    <div key={ledger._id} className={`rounded-2xl shadow-xl border-l-4 overflow-hidden transform transition-all duration-300 hover:scale-[1.02] ${
                      ledger.isActive !== false 
                        ? (darkMode ? 'bg-gray-800/95 border-blue-500' : 'bg-white border-blue-500')
                        : (darkMode ? 'bg-gray-800/60 border-red-400 opacity-80' : 'bg-gray-100 border-red-400 opacity-80')
                    }`}>
                      {/* Header Section */}
                      <div className={`px-4 py-3.5 border-b ${
                        darkMode ? 'bg-gray-900/60 border-gray-700/50' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
                      }`}>
                        <div className="flex justify-between items-start gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-bold text-lg mb-1.5 truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {ledger.Name}
                            </h3>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              darkMode ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30' : 'bg-blue-100 text-blue-800 border border-blue-200'
                            }`}>
                              {ledger.id}
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-1.5">
                            <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap shadow-sm ${
                              ledger.isActive !== false 
                                ? (darkMode ? 'bg-green-600/30 text-green-300 border border-green-500/30' : 'bg-green-100 text-green-800 border border-green-200')
                                : (darkMode ? 'bg-red-600/30 text-red-300 border border-red-500/30' : 'bg-red-100 text-red-800 border border-red-200')
                            }`}>
                              {ledger.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="px-4 py-4">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className={`p-3 rounded-xl ${
                            darkMode ? 'bg-gray-900/40 border border-gray-700/50' : 'bg-gray-50 border border-gray-200'
                          }`}>
                            <p className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              GSTIN
                            </p>
                            <p className={`text-sm font-bold break-all leading-tight ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {ledger.GSTIN || 'N/A'}
                            </p>
                          </div>
                          <div className={`p-3 rounded-xl ${
                            darkMode ? 'bg-gray-900/40 border border-gray-700/50' : 'bg-gray-50 border border-gray-200'
                          }`}>
                            <p className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              GST Type
                            </p>
                            <p className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {ledger.gstRegistrationType || 'Regular'}
                            </p>
                          </div>
                        </div>
                        
                        <div className={`rounded-xl p-4 mb-4 border ${
                          darkMode ? 'bg-gradient-to-br from-gray-900/60 to-gray-900/40 border-gray-700/50' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                        }`}>
                          <p className={`text-xs font-semibold mb-2 uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Closing Balance
                          </p>
                          <p className={`font-extrabold text-2xl ${
                            ledger.closingBalance > 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : 
                            ledger.closingBalance < 0 ? (darkMode ? 'text-red-400' : 'text-red-600') : 
                            (darkMode ? 'text-gray-400' : 'text-gray-500')
                          }`}>
                            ₹ {(ledger.closingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                      
                      {/* Actions Section */}
                      <div className={`px-4 py-3.5 flex flex-wrap gap-2 border-t ${
                        darkMode ? 'border-gray-700/50 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <button 
                          onClick={() => openDetailModal(ledger)}
                          className={`flex-1 min-w-[110px] px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 transform active:scale-95 flex items-center justify-center gap-2 shadow-md ${
                            darkMode 
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-blue-500/30'
                              : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-500/50'
                          }`}
                        >
                          <FaEye className="h-4 w-4" />
                          <span>Details</span>
                        </button>
                        {ledger.isActive !== false && (
                          <button 
                            onClick={() => openRecipientModal(ledger)}
                            className={`flex-1 min-w-[110px] px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 transform active:scale-95 flex items-center justify-center gap-2 shadow-md ${
                              darkMode
                                ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white shadow-green-500/30'
                                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-green-500/50'
                            }`}
                          >
                            <FaUserPlus className="h-4 w-4" />
                            <span>Recipient</span>
                          </button>
                        )}
                        <button 
                          onClick={() => toggleLedgerStatus(ledger)}
                          className={`${ledger.isActive !== false ? 'flex-1' : 'w-full'} min-w-[110px] px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 transform active:scale-95 flex items-center justify-center gap-2 shadow-md whitespace-nowrap ${
                            ledger.isActive !== false
                              ? (darkMode 
                                  ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-red-500/30'
                                  : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-red-500/50')
                              : (darkMode
                                  ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white shadow-green-500/30'
                                  : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-green-500/50')
                          }`}
                        >
                          {ledger.isActive !== false ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Desktop view with simplified table */}
              <div className="hidden lg:block overflow-x-auto rounded-2xl">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>ID</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Name</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>GSTIN</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>GST Type</th>
                      <th className={`px-6 py-4 text-right text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Closing Balance</th>
                      <th className={`px-6 py-4 text-center text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Status</th>
                      <th className={`px-6 py-4 text-center text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {tallyLedgers.length > 0 ? (
                      tallyLedgers.map((ledger) => (
                        <tr key={ledger._id} className={`transition-colors ${
                          ledger.isActive === false 
                            ? (darkMode ? 'bg-red-900/20 opacity-75' : 'bg-red-50 opacity-75')
                            : (darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50')
                        }`}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{ledger.id}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledger.Name}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{ledger.GSTIN || 'N/A'}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{ledger.gstRegistrationType || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span className={`font-bold ${
                              ledger.closingBalance > 0 ? 'text-green-500' : 
                              ledger.closingBalance < 0 ? 'text-red-500' : 
                              (darkMode ? 'text-gray-400' : 'text-gray-500')
                            }`}>
                              ₹ {(ledger.closingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                              ledger.isActive !== false 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {ledger.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                            <div className="flex justify-center gap-2">
                              <button 
                                onClick={() => openDetailModal(ledger)}
                                className={`px-3 py-2 rounded-xl font-medium transition-all shadow-sm ${
                                  darkMode
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                }`}
                                title="View Details"
                              >
                                <FaEye className="inline mr-1" /> Details
                              </button>
                              {ledger.isActive !== false && (
                                <button 
                                  onClick={() => openRecipientModal(ledger)}
                                  className={`px-3 py-2 rounded-xl font-medium transition-all shadow-sm ${
                                    darkMode
                                      ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-md'
                                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                                  }`}
                                  title="Select Recipient"
                                >
                                  <FaUserPlus className="inline mr-1" /> Recipient
                                </button>
                              )}
                              <button 
                                onClick={() => toggleLedgerStatus(ledger)}
                                className={`px-3 py-2 rounded-xl font-medium transition-all shadow-sm ${
                                  ledger.isActive !== false
                                    ? (darkMode 
                                      ? 'bg-red-600 text-white hover:bg-red-700 hover:shadow-md'
                                      : 'bg-red-50 text-red-700 hover:bg-red-100')
                                    : (darkMode
                                      ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-md'
                                      : 'bg-green-50 text-green-700 hover:bg-green-100')
                                }`}
                                title={ledger.isActive !== false ? 'Deactivate Ledger' : 'Activate Ledger'}
                              >
                                {ledger.isActive !== false ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className={`px-6 py-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <FaFilter className={`mx-auto text-5xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                          <p className="text-lg font-medium">No tally ledgers found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center mt-8 gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`p-2.5 rounded-lg font-medium transition-all ${
                      currentPage === 1 
                        ? (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        : (darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-md hover:shadow-lg'
                          : 'bg-white hover:bg-gray-50 text-gray-700 shadow-sm hover:shadow-md border border-gray-300')
                    }`}
                  >
                    <FaChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="flex gap-2">
                    {renderPaginationButtons()}
                  </div>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-2.5 rounded-lg font-medium transition-all ${
                      currentPage === totalPages 
                        ? (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        : (darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-md hover:shadow-lg'
                          : 'bg-white hover:bg-gray-50 text-gray-700 shadow-sm hover:shadow-md border border-gray-300')
                    }`}
                  >
                    <FaChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Modals */}
      {detailModalOpen && <DetailModal />}
      {recipientModalOpen && <RecipientModal />}
      
      {/* New Recipient Selection Modal */}
      <MasterSelection
        isOpen={recipientSelectionOpen}
        onClose={() => setRecipientSelectionOpen(false)}
        onSelect={handleRecipientSelect}
        allowedTypes={['Customer', 'Dealer']}
        defaultType="Customer"
        title={`Select Recipient for ${selectedLedger?.Name || 'Ledger'}`}
      />
      </div>
    </div>
  );
};

export default TallyLedgerManagement;